import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { AuthContext } from "../../Header/auth - context";
import { uzmiGresku } from "../../custom/greske";
import PregledDokumenata from "../../custom/PregledDokumenata";

/* Dokumentacija radnog naloga — ono što vozač slika sa telefona.

   ZAŠTO POSTOJI
   Telefon je dokumenta slao od početka (`/trip/mobileRnDokument`), a web ruta
   `/trip/rnDokumenta` je stajala napravljena i **niko je nije zvao** — pa CMR,
   termo ispis i slike oštećenja nisu se u TMS-u videle nigde. Vozač šalje, a
   dispečer nema gde da pogleda.

   Prikaz je NAMERNO samo čitanje: dokument je dokaz šta je zatečeno na terenu,
   pa se iz TMS-a ne briše i ne menja. Brisanje postoji samo na telefonu, i to
   vozaču nad sopstvenim zapisom, dok nalog traje.

   Preglednik je zajednički `PregledDokumenata` (isti kao kod vozila i faktura),
   da se deset priloga ne otvara u deset tabova. */

const RnDokumenta = ({ idWorkLog, brojNaloga }) => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);

    const [dokumenta, setDokumenta] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [pregledIndeks, setPregledIndeks] = useState(null);

    useEffect(() => {
        if (!idWorkLog) { setDokumenta([]); return; }
        let ziv = true;
        setUcitava(true);
        fetch(process.env.REACT_APP_BACKEND_URL + "/trip/rnDokumenta", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify({ id_radni_nalog: idWorkLog }),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(d => { if (ziv) setDokumenta(Array.isArray(d?.dokumenta) ? d.dokumenta : []); })
            .catch(async (greska) => {
                if (!ziv) return;
                setDokumenta([]);
                toast.current?.show({ severity: "error", summary: "Error", detail: await uzmiGresku(greska), life: 3000 });
            })
            .finally(() => { if (ziv) setUcitava(false); });
        return () => { ziv = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idWorkLog]);

    /* Putanja iz baze je Windows oblika i vodi u deljeni folder — pretvara se u
       adresu koju servira backend. Isti postupak kao kod dokumenata vozila. */
    const adresaDokumenta = (putanja) => {
        if (!putanja) return null;
        const relativna = String(putanja).replace(/\\/g, "/").replace(/^C:\/Dokumenta\//, "").replace(/^\/+/, "");
        return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(relativna)}`;
    };

    /* Preglednik i tabela rade nad ISTIM nizom, pa je redosled listanja isti kao
       redosled redova na ekranu. `fajl` je ime koje preglednik čita da bi znao
       format (slika / PDF / ostalo). */
    const zaPregled = dokumenta.map(d => ({ ...d, fajl: d.naziv_fajla, url: adresaDokumenta(d.putanja) }));

    const linkFajla = (red) => {
        if (!adresaDokumenta(red.putanja)) return "-";
        return (
            <Button
                label={red.naziv_fajla || t("ture.rnDokOtvori")}
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                /* Pozicija se traži po samom zapisu, ne po rowIndex-u: rowIndex je
                   redni broj u FILTRIRANOM prikazu, pa bi uz aktivnu pretragu
                   strelice listale pogrešne dokumente. */
                onClick={() => setPregledIndeks(zaPregled.findIndex(x => x.id === red.id))}
                tooltip={t("admin.pregledaj")}
                tooltipOptions={{ position: "top" }}
            />
        );
    };

    /* Odakle je dokument stigao se vidi na prvi pogled: dispečeru je razlika
       između „vozač je slikao na terenu" i „neko je dodao sa računara" bitna
       kad se dokument koristi kao dokaz. */
    const oznakaIzvora = (red) => (
        <Tag value={red.izvor === "MOBILNI" ? t("ture.rnDokTelefon") : t("ture.rnDokRacunar")}
             severity={red.izvor === "MOBILNI" ? "info" : "secondary"} />
    );

    const zaglavlje = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <span className="pregled-brojac">
                    {t("ture.rnDokBroj", { broj: dokumenta.length })}
                </span>
            </div>
            <input
                type="search"
                onInput={(e) => setGlobalFilter(e.target.value)}
                placeholder={t("nova.phPretraga")}
                className="p-inputtext p-component"
            />
        </div>
    );

    return (
        <div className="zaposleni_table_container" style={{ marginTop: "1%" }}>
            <Toast ref={toast} />

            <DataTable
                className="stavke-tabela"
                value={zaPregled}
                paginator rows={10}
                header={zaglavlje}
                globalFilter={globalFilter}
                loading={ucitava}
                scrollable scrollHeight="50vh"
                resizableColumns columnResizeMode="expand"
                emptyMessage={idWorkLog ? t("ture.rnDokNema") : t("ture.rnDokIzaberi")}
            >
                <Column field="vrsta" header={t("nova.vrstaDokumenta")} sortable style={{ minWidth: "170px" }}
                        body={(r) => r.vrsta || "-"} />
                <Column field="naziv_fajla" header={t("nova.dokument")} style={{ minWidth: "220px" }}
                        body={linkFajla} />
                <Column field="izvor" header={t("kazneEv.izvor")} sortable style={{ minWidth: "120px" }}
                        body={oznakaIzvora} />
                <Column field="autor" header={t("ture.rnDokAutor")} sortable style={{ minWidth: "180px" }}
                        body={(r) => r.autor || "-"} />
                <Column field="datum" header={t("ture.vremeUnosa")} sortable style={{ minWidth: "150px" }} />
                <Column field="napomena" header={t("nova.napomena")} style={{ minWidth: "220px" }}
                        body={(r) => r.napomena || "-"} />
            </DataTable>

            <PregledDokumenata
                dokumenta={zaPregled}
                indeks={pregledIndeks}
                naslov={brojNaloga || ""}
                onIndeks={setPregledIndeks}
                onZatvori={() => setPregledIndeks(null)}
            />
        </div>
    );
};

export default RnDokumenta;
